import { useState, useEffect } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent
  }
}

// Capture event at module level — fires once before React mounts
let _deferredPrompt: BeforeInstallPromptEvent | null = null

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    _deferredPrompt = e
    window.dispatchEvent(new CustomEvent('pwa-install-available'))
  })
}

export function usePWAInstall() {
  const [canInstall, setCanInstall] = useState(_deferredPrompt !== null)

  useEffect(() => {
    const onAvailable = () => setCanInstall(true)
    const onInstalled = () => { _deferredPrompt = null; setCanInstall(false) }
    window.addEventListener('pwa-install-available', onAvailable)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('pwa-install-available', onAvailable)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!_deferredPrompt) return 'unavailable'
    await _deferredPrompt.prompt()
    const { outcome } = await _deferredPrompt.userChoice
    _deferredPrompt = null
    setCanInstall(false)
    return outcome
  }

  return { canInstall, promptInstall }
}
